import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Alert,
  Badge,
  Card,
  InputGroup,
  FormControl,
  Pagination,
  Table
} from 'react-bootstrap';

function AdminArticles({ user }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    article_type: ''
  });

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [pagination.page, filters]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = await axios.get('/admin/articles', {
        params,
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });

      if (response.data.status) {
        setArticles(response.data.data.articles);
        setPagination(prev => ({
          ...prev,
          ...response.data.data.pagination
        }));
      }
    } catch (err) {
      setError('Failed to fetch articles');
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/admin/articles/categories', {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });
      if (response.data.status) setCategories(response.data.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      const response = await axios.delete(`/admin/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (response.data.status) {
        setSuccess('Article deleted successfully');
        fetchArticles();
      }
    } catch (err) {
      setError('Failed to delete article');
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.pages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === pagination.page}
          onClick={() => setPagination(prev => ({ ...prev, page }))}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < pagination.pages) {
      if (endPage < pagination.pages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item
          key={pagination.pages}
          onClick={() => setPagination(prev => ({ ...prev, page: pagination.pages }))}
        >
          {pagination.pages}
        </Pagination.Item>
      );
    }

    return items;
  };

  if (user.isLoading) {
    return (
      <div id='fullHeight'>
        <div id='loading' />
      </div>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <h1 className="h3 mb-0">Article Management</h1>
        </Col>
        <Col className="text-end">
          <Button onClick={() => navigate('/admin-panel/articles/create')}>➕ Create New Article</Button>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <FormControl
                  placeholder="Search articles..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.article_type}
                onChange={(e) => handleFilterChange('article_type', e.target.value)}
              >
                <option value="">All Content Types</option>
                <option value="article">Article</option>
                <option value="breaking-news">Breaking News</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Articles Table */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div id='fullHeight'>
              <div id='loading' />
            </div>
          ) : articles.length > 0 ? (
            <>
              <div style={{ minHeight: '600px', maxHeight: '800px', overflowY: 'auto' }}>
                <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '4%' }}>#</th>
                    <th style={{ width: '7%' }}>Thumbnail</th>
                    <th style={{ width: '22%' }}>Title</th>
                    <th style={{ width: '10%' }}>Sports</th>
                    <th style={{ width: '10%' }}>Type</th>
                    <th style={{ width: '9%' }}>Status</th>
                    <th style={{ width: '12%' }}>Created</th>
                    <th style={{ width: '12%' }}>Published</th>
                    <th style={{ width: '14%' }} className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((article, index) => (
                    <tr key={article.id}>
                      <td className="align-middle">
                        {(pagination.page - 1) * pagination.limit + index + 1}
                      </td>
                      <td className="align-middle">
                        {article.thumbnail ? (
                          <img
                            src={article.thumbnail}
                            alt={article.title}
                            style={{
                              width: '60px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #dee2e6'
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '60px',
                              height: '40px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #dee2e6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px',
                              color: '#adb5bd'
                            }}
                          >
                            📷
                          </div>
                        )}
                      </td>
                      <td className="align-middle">
                        <div className="fw-semibold">{article.title}</div>
                        <small className="text-muted">{article.slug}</small>
                      </td>
                      <td className="align-middle">
                        <Badge bg="info">{article.category || '-'}</Badge>
                      </td>
                      <td className="align-middle">
                        <Badge bg={article.article_type === 'breaking-news' ? 'danger' : 'primary'}>
                          {article.article_type === 'breaking-news' ? 'Breaking News' : 'Article'}
                        </Badge>
                      </td>
                      <td className="align-middle">
                        <Badge bg={article.status === 'published' ? 'success' : 'secondary'}>
                          {article.status}
                        </Badge>
                      </td>
                      <td className="align-middle">
                        <small>{moment(article.date_created).format('MMM DD, YYYY')}</small>
                        <br />
                        <small className="text-muted">{moment(article.date_created).format('h:mm A')}</small>
                      </td>
                      <td className="align-middle">
                        {article.publish_date ? (
                          <>
                            <small>{moment(article.publish_date).format('MMM DD, YYYY')}</small>
                            <br />
                            <small className="text-muted">{moment(article.publish_date).format('h:mm A')}</small>
                          </>
                        ) : (
                          <small className="text-muted">Not published</small>
                        )}
                      </td>
                      <td className="align-middle text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate(`/admin-panel/articles/edit/${article.id}`)}
                            title="Edit"
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(article.id)}
                            title="Delete"
                          >
                            🗑️
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>

              {/* Pagination Info and Controls */}
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted small">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} articles
                </div>
                {pagination.pages > 1 && (
                  <Pagination className="mb-0">
                    <Pagination.First
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                    />
                    <Pagination.Prev
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={pagination.page === pagination.pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    />
                    <Pagination.Last
                      disabled={pagination.page === pagination.pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: pagination.pages }))}
                    />
                  </Pagination>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <div style={{ fontSize: 48, opacity: 0.3 }}>📝</div>
              <h5 className="mt-3 mb-2">No articles found</h5>
              <p className="text-muted mb-3">Create your first article to get started</p>
              <Button variant="primary" onClick={() => navigate('/admin-panel/articles/create')}>
                ➕ Create Article
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(AdminArticles);
